import axios from 'axios'
import type { ApiPayload } from '../types/aicall'
import { toastError } from './errorUtils'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3008/api'

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 120000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  },
  (error) => Promise.reject(error)
)

// --- Token refresh + retry ---
let isRefreshing = false
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = []

function drainQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)))
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    if (error.response?.status === 500) {
      console.error('Erro interno do servidor:', error.response.data)
    }

    // Not a 401, or already retried — pass through
    if (error.response?.status !== 401 || original?._retry) {
      if (error.response?.status !== 401) {
        toastError(error, 'Erro na requisição')
      }
      return Promise.reject(error)
    }

    const currentToken = localStorage.getItem('auth_token')
    const refreshToken = localStorage.getItem('refresh_token')

    // No credentials to refresh with — reject immediately
    if (!currentToken && !refreshToken) {
      return Promise.reject(error)
    }

    // Another refresh is already in flight — queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then((token) => {
        original.headers = original.headers || {}
        original.headers.Authorization = `Bearer ${token}`
        return api(original)
      })
    }

    original._retry = true
    isRefreshing = true

    try {
      // Use plain axios (not api instance) to avoid infinite interception
      const { data } = await axios.post(
        `${BASE_URL}/auth/refresh`,
        { access_token: currentToken, refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      )

      const newToken: string = data.access_token
      const newRefresh: string | null = data.refresh_token ?? null

      localStorage.setItem('auth_token', newToken)
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh)

      drainQueue(null, newToken)

      original.headers = original.headers || {}
      original.headers.Authorization = `Bearer ${newToken}`
      return api(original)
    } catch (refreshError) {
      drainQueue(refreshError, null)
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export const apiService = {
  // Autenticação
  getAuthConfig: () => api.get('/auth/config'),
  register: (data: Record<string, unknown>) => api.post('/auth/register', data),
  login: (data: Record<string, unknown>) => api.post('/auth/login', data),
  getMe: (email?: string) => api.get('/auth/me', { params: email ? { email } : undefined }),

  // ElevenLabs
  testConnection: () => api.get('/elevenlabs/test'),
  getUserInfo: () => api.get('/elevenlabs/user'),

  // Agentes
  getAgents: () => api.get('/agents'),
  getAgent: (id: string) => api.get(`/agents/${id}`),
  getAgentVoices: () => api.get('/agents/voices'),
  createAgent: (data: ApiPayload) => api.post('/agents', data),
  updateAgent: (id: string, data: ApiPayload) => api.patch(`/agents/${id}`, data),
  deleteAgent: (id: string) => api.delete(`/agents/${id}`),

  // Números de telefone
  getPhoneNumbers: () => api.get('/phone-numbers'),
  getPhoneNumber: (id: string) => api.get(`/phone-numbers/${id}`),
  createPhoneNumber: (data: ApiPayload) => api.post('/phone-numbers', data),
  updatePhoneNumber: (id: string, data: ApiPayload) => api.patch(`/phone-numbers/${id}`, data),
  deletePhoneNumber: (id: string) => api.delete(`/phone-numbers/${id}`),

  // Ligações
  makeCall: (data: ApiPayload) => api.post('/calls', data),
  preflightCall: (data: ApiPayload) => api.post('/calls/preflight', data),
  getCalls: (agentId: string, limit = 20, offset = 0, page?: number) =>
    api.get(`/calls/agent/${agentId}`, { params: { limit, offset, page } }),
  createBatchCalls: (data: ApiPayload) => api.post('/calls/batch', data),
  getBatchJob: (batchId: string) => api.get(`/calls/batch/${batchId}`),
  getCallStats: (agentId: string, startDate: string, endDate: string) =>
    api.get(`/calls/stats/${agentId}`, { params: { startDate, endDate } }),
  pauseCall: (callId: string) => api.post(`/calls/${callId}/pause`),
  endCall: (callId: string) => api.post(`/calls/${callId}/end`),
  startCall: (callId: string) => api.post(`/calls/${callId}/start`),

  // Conversas
  getConversations: (agentId: string, limit = 100, offset = 0, language?: string) =>
    api.get(`/conversations/agent/${agentId}`, { params: { limit, offset, language } }),
  getConversation: (agentId: string, conversationId: string) =>
    api.get(`/conversations/${agentId}/${conversationId}`),
  getConversationOverview: (agentId: string, conversationId: string) =>
    api.get(`/conversations/${agentId}/${conversationId}/overview`),
  createConversation: (agentId: string, data: ApiPayload) =>
    api.post(`/conversations/${agentId}`, data),
  getConversationAudio: (agentId: string, conversationId: string) =>
    api.get(`/conversations/${agentId}/${conversationId}/audio`, { responseType: 'blob' }),

  // Evolution API
  getEvolutionStatus: () => api.get('/evolution/status'),
  getEvolutionInstances: () => api.get('/evolution/instances'),
  createEvolutionInstance: (data: { instanceName: string }) =>
    api.post('/evolution/instances', data),
  getEvolutionQrCode: (instance: string) =>
    api.get(`/evolution/instances/${instance}/qrcode`),
  getEvolutionConnectionState: (instance: string) =>
    api.get(`/evolution/instances/${instance}/connection-state`),
  deleteEvolutionInstance: (instance: string) =>
    api.delete(`/evolution/instances/${instance}`),
  getEvolutionInstance: (instance: string) =>
    api.get(`/evolution/instances/${instance}`),
  sendEvolutionText: (instance: string, data: ApiPayload) =>
    api.post(`/evolution/${instance}/messages/text`, data),
  setEvolutionWebhook: (instance: string, data: ApiPayload) =>
    api.post(`/evolution/${instance}/webhook`, data),

  // Wavoip provisioning (Evolution + ElevenLabs)
  createWavoipNumber: (data: { label?: string; assigned_agent?: string }) =>
    api.post('/phone-numbers/wavoip', data),
  getWavoipStatus: (providerConnectionId: string) =>
    api.get(`/phone-numbers/wavoip/status/${providerConnectionId}`),
  regenerateWavoipQr: (providerConnectionId: string) =>
    api.post(`/phone-numbers/wavoip/${providerConnectionId}/qr`),
  disconnectWavoipNumber: (providerConnectionId: string) =>
    api.post(`/phone-numbers/wavoip/${providerConnectionId}/disconnect`),
  deleteWavoipNumber: (id: string) =>
    api.delete(`/phone-numbers/${id}/cascade`),
  transferPhoneNumber: (id: string, data: { target_organization_id: string; assigned_agent?: string }) =>
    api.patch(`/phone-numbers/${id}/transfer`, data),

  // Sincronia
  getTelephonySync: () => api.get('/sync/telephony'),

  // Pipeline operacional
  getPipelineBoard: (refresh = false) =>
    api.get('/pipeline/board', { params: refresh ? { refresh: 1 } : undefined }),
  syncPipeline: (limit = 50) => api.post('/pipeline/sync', { limit }),
  movePipelineCard: (cardId: string, data: { column_id: string; position: number }) =>
    api.patch(`/pipeline/cards/${encodeURIComponent(cardId)}/move`, data),
  updatePipelineCard: (cardId: string, data: { next_action?: string; notes?: string; tags?: string[] }) =>
    api.patch(`/pipeline/cards/${encodeURIComponent(cardId)}`, data),
  preflightPipelineCard: (cardId: string) =>
    api.post(`/pipeline/cards/${encodeURIComponent(cardId)}/preflight`),

  // Administrativo SaaS
  getAdminSummary: () => api.get('/admin/summary'),
  getAdminDatabase: () => api.get('/admin/database'),
  getAdminRoles: () => api.get('/admin/roles'),
  getAdminOrganizations: () => api.get('/admin/organizations'),
  createAdminOrganization: (data: Record<string, unknown>) => api.post('/admin/organizations', data),
  updateAdminOrganization: (id: string, data: Record<string, unknown>) =>
    api.patch(`/admin/organizations/${id}`, data),
  deleteAdminOrganization: (id: string) => api.delete(`/admin/organizations/${id}`),
  getAdminUsers: () => api.get('/admin/users'),
  createAdminUser: (data: Record<string, unknown>) => api.post('/admin/users', data),
  updateAdminUser: (id: string, data: Record<string, unknown>) => api.patch(`/admin/users/${id}`, data),
  deleteAdminUser: (id: string) => api.delete(`/admin/users/${id}`),
  banAdminUser: (id: string) => api.post(`/admin/users/${id}/ban`),
  getProviderConnections: () => api.get('/admin/provider-connections'),
  createProviderConnection: (data: Record<string, unknown>) => api.post('/admin/provider-connections', data),
  updateProviderConnection: (id: string, data: Record<string, unknown>) =>
    api.patch(`/admin/provider-connections/${id}`, data),
  testProviderConnection: (id: string, data: Record<string, unknown>) =>
    api.post(`/admin/provider-connections/${id}/test`, data),
  deleteProviderConnection: (id: string) => api.delete(`/admin/provider-connections/${id}`),
  getImpersonations: () => api.get('/admin/impersonations'),
  startImpersonation: (data: Record<string, unknown>) => api.post('/admin/impersonations', data),
  endImpersonation: (id: string) => api.post(`/admin/impersonations/${id}/end`),
  getAuditLogs: () => api.get('/admin/audit-logs'),

  // Integrações do workspace do usuário
  getDeveloperSummary: () => api.get('/developer/summary'),
  getDeveloperApiKeys: () => api.get('/developer/api-keys'),
  createDeveloperApiKey: (data: Record<string, unknown>) => api.post('/developer/api-keys', data),
  revokeDeveloperApiKey: (id: string) => api.delete(`/developer/api-keys/${id}`),
  getDeveloperWebhookEndpoints: () => api.get('/developer/webhook-endpoints'),
  createDeveloperWebhookEndpoint: (data: Record<string, unknown>) => api.post('/developer/webhook-endpoints', data),
  updateDeveloperWebhookEndpoint: (id: string, data: Record<string, unknown>) =>
    api.patch(`/developer/webhook-endpoints/${id}`, data),
  deleteDeveloperWebhookEndpoint: (id: string) => api.delete(`/developer/webhook-endpoints/${id}`),
  getDeveloperMcp: () => api.get('/developer/mcp'),

  // Cobrança e uso
  getBillingPlans: () => api.get('/billing/plans'),
  getBillingSubscription: () => api.get('/billing/subscription'),
  getBillingCheckStatus: () => api.get('/billing/check-status'),
  createBillingCheckout: (data: Record<string, unknown>) => api.post('/billing/checkout', data),
  getBillingUsage: () => api.get('/billing/usage'),
  recordBillingUsage: (data: Record<string, unknown>) => api.post('/billing/usage/events', data),

  // Onboarding
  getOnboardingStatus: () => api.get('/onboarding/status'),
  saveOnboardingProfile: (data: Record<string, unknown>) => api.post('/onboarding/profile', data),
  saveOnboardingProvider: (data: Record<string, unknown>) => api.post('/onboarding/provider', data),
  saveOnboardingElevenlabs: (data: Record<string, unknown>) => api.post('/onboarding/elevenlabs', data),
  completeOnboarding: () => api.post('/onboarding/complete'),

  // Convites
  getInvitationInfo: (token: string) => api.get('/invitations/info', { params: { token } }),
  acceptInvitation: (token: string) => api.post('/invitations/accept', { token }),
  registerForInvite: (data: { email: string; password: string; name: string; token: string }) =>
    api.post('/invitations/register', data),
  getAdminInvitations: () => api.get('/admin/invitations'),
  createAdminInvitation: (data: Record<string, unknown>) => api.post('/admin/invitations', data),
  resendAdminInvitation: (id: string) => api.post(`/admin/invitations/${id}/resend`),
  revokeAdminInvitation: (id: string) => api.post(`/admin/invitations/${id}/revoke`),

  // Configurações da Plataforma
  getPlatformConfig: () => api.get('/admin/platform-config'),
  updatePlatformConfig: (data: { key: string; value: string }) => api.post('/admin/platform-config', data),

  // Admin billing
  getAdminBillingSubscriptions: (params?: Record<string, unknown>) => api.get('/admin/billing/subscriptions', { params }),
  getAdminBillingOrgDetail: (orgId: string) => api.get(`/admin/billing/subscriptions/${orgId}`),
  activateAdminBillingSubscription: (orgId: string, data?: Record<string, unknown>) =>
    api.post(`/admin/billing/subscriptions/${orgId}/activate`, data),
  deactivateAdminBillingSubscription: (orgId: string, data?: Record<string, unknown>) =>
    api.post(`/admin/billing/subscriptions/${orgId}/deactivate`, data),
  changeAdminBillingPlan: (orgId: string, data: Record<string, unknown>) =>
    api.patch(`/admin/billing/subscriptions/${orgId}/plan`, data),
  extendAdminBillingPeriod: (orgId: string, data: Record<string, unknown>) =>
    api.post(`/admin/billing/subscriptions/${orgId}/extend`, data),
  getAdminBillingManualLogs: (params?: Record<string, unknown>) => api.get('/admin/billing/manual-logs', { params }),
  getAdminBillingBlockedLogs: (params?: Record<string, unknown>) => api.get('/admin/billing/blocked-logs', { params }),
  updateAdminBillingLimits: (orgId: string, data: { limits: Record<string, number> }) =>
    api.post(`/admin/billing/subscriptions/${orgId}/limits`, data),
}

export default api
